<script lang="ts">
  /** Pick a visualization: filter by kind, search, grouped grid. Hidden ones are left out. */
  import type { AppController } from '../../app/controller.svelte';
  import { vizRole, type VizRole } from '../../shared/viz';

  interface Props {
    ctl: AppController;
    current: string;
    onpick: (vizId: string) => void;
  }
  let { ctl, current, onpick }: Props = $props();
  let query = $state('');
  let showMilkdrop = $state(false);
  let role = $state<VizRole | 'all'>('all');

  /** Built-in and ISF always; MilkDrop presets only when searching or expanded (there are hundreds). */
  const pickable = $derived.by(() => {
    const q = query.trim().toLowerCase();
    const all = ctl.browsableViz;
    const match = (v: (typeof all)[number]) =>
      !q || `${v.name} ${v.description} ${(v.tags ?? []).join(' ')}`.toLowerCase().includes(q);
    const main = all.filter(
      (v) => v.kind !== 'butterchurn' && match(v) && (role === 'all' || vizRole(v) === role),
    );
    const md = all.filter((v) => v.kind === 'butterchurn');
    const mdShown = (q || showMilkdrop) && role !== 'element' ? md.filter(match).slice(0, 120) : [];
    const byRole = (r: VizRole) => main.filter((v) => vizRole(v) === r);
    return {
      list: [...byRole('background'), ...byRole('element'), ...mdShown],
      mdTotal: md.length,
    };
  });
</script>

<div class="seg" role="group" aria-label="Show">
  {#each [['all', 'All'], ['background', 'Full-screen'], ['element', 'Shapes & particles']] as [value, label] (value)}
    <button
      class:active={role === value}
      aria-pressed={role === value}
      data-testid={`vizfilter-${value}`}
      onclick={() => (role = value as typeof role)}>{label}</button
    >
  {/each}
</div>
<input
  type="search"
  class="viz-search"
  placeholder="Search visualizations"
  bind:value={query}
  aria-label="Search visualizations"
/>
<div class="viz-grid" role="radiogroup" aria-label="Visualization">
  {#each pickable.list as v, i (v.id)}
    {#if role === 'all' && v.kind !== 'butterchurn' && (i === 0 || vizRole(pickable.list[i - 1]) !== vizRole(v))}
      <h3 class="group">
        {vizRole(v) === 'element'
          ? 'Shapes & particles: drawn on black, good for layering'
          : 'Full-screen'}
      </h3>
    {/if}
    <button
      role="radio"
      aria-checked={v.id === current}
      class:active={v.id === current}
      data-testid={`viz-${v.id}`}
      title={v.description}
      onclick={() => onpick(v.id)}
      >{v.name}{#if v.kind && v.kind !== 'native'}<span class="chip"
          >{v.kind === 'isf' ? 'ISF' : 'MilkDrop'}</span
        >{/if}</button
    >
  {/each}
</div>
{#if pickable.mdTotal > 0 && !query}
  <button class="link" onclick={() => (showMilkdrop = !showMilkdrop)}
    >{showMilkdrop ? 'Hide' : 'Show'} {pickable.mdTotal} MilkDrop presets</button
  >
{/if}

<style>
  .seg {
    display: flex;
    gap: 4px;
    margin-bottom: 8px;
  }
  .seg button {
    flex: 1;
    font-size: 11.5px;
    padding: 3px 4px;
  }
  .viz-search {
    width: 100%;
    margin-bottom: 8px;
  }
  .viz-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
    margin-bottom: 10px;
    max-height: 300px;
    overflow-y: auto;
  }
  .viz-grid button {
    text-align: left;
    font-size: 12.5px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 4px;
  }
  .viz-grid .group {
    grid-column: 1 / -1;
    margin: 6px 0 0;
    font-size: 11px;
    font-weight: 500;
    color: var(--muted);
  }
</style>
